import { DataWithNoticeType } from 'lib/helpers';
import { getModelFromSnapshot } from 'lib/model';
import { UserModel } from 'lib/model/objects/userModel';
import { ESnapshot, EOrganization, EUser, exists } from 'lib/types';
import { CustomNoticeFilingType } from 'lib/types/filingType';
import {
  disableNonPublishingDays,
  publishingDayEnumValuesFromDeadlines,
  getIsAfterPublishingDeadline,
  isAllowedPublicationDay
} from 'lib/utils/deadlines';
import moment from 'moment';
import { getFirebaseContext } from 'utils/firebase';
import { userIsSuper } from 'utils/permissions';

export const SATURDAY_DAY_INDEX = 6;
export const SUNDAY_DAY_INDEX = 0;

/**
 * removeDatesFromArray helper function deletes dates from an array of dates
 * @param pubDates array of dates
 * @param itemsToRemove array of items to remove from pubDates
 * @returns a new array with items removed
 */
export const removeDatesFromArray = (
  pubDates: Date[],
  itemsToRemove: Date[]
): Date[] => pubDates.filter(date => !itemsToRemove.includes(date));

/**
 * removeRowClickForWeekendEdition deletes dates from an array of publication dates and makes sure weekend dates are always deleted together
 * (ex: deleting Saturday deletes Sunday as well)
 *
 * @param publicationDates array containing publication dates
 * @param i row clicked in app, the index of the element in publicationDates array
 * @returns a new array removing weekends from publicationDates
 */
export const removeRowClickForWeekendEdition = (
  publicationDates: Date[],
  i: number
): Date[] => {
  // Remove both weekend days if publication date clicked is a Sunday
  if (moment(publicationDates[i]).day() === SATURDAY_DAY_INDEX) {
    return removeDatesFromArray(publicationDates, [
      publicationDates[i],
      publicationDates[i + 1]
    ]);
    // Remove both weekend days if publication date clicked is a Sunday
  }
  if (moment(publicationDates[i]).day() === SUNDAY_DAY_INDEX) {
    return removeDatesFromArray(publicationDates, [
      publicationDates[i - 1],
      publicationDates[i]
    ]);
    // Remove the weekday
  }
  return removeDatesFromArray(publicationDates, [publicationDates[i]]);
};

/**
 * addPublicationDates adds dates to an array of publication dates ensuring weekends are added together
 * (ex. adding SAturday adds Sunday as well)
 *
 * @param selectedPublicationDate date selected in date picker
 * @param publicationDates array containing publication dates
 * @returns a new array removing weekends from publicationDates
 */
export const addPublicationDates = (
  selectedPublicationDate: Date,
  publicationDates: Date[]
): Date[] => {
  if (moment(selectedPublicationDate).day() === SATURDAY_DAY_INDEX) {
    const sunday = moment(selectedPublicationDate).day(7).toDate();
    return [...publicationDates, selectedPublicationDate, sunday];
  }
  if (moment(selectedPublicationDate).day() === SUNDAY_DAY_INDEX) {
    const saturday = moment(selectedPublicationDate).day(-1).toDate();
    return [...publicationDates, saturday, selectedPublicationDate];
  }
  return [...publicationDates, selectedPublicationDate];
};

/**
 * removeDuplicateDates deletes duplicate dates from an array
 * @param publicationDates an array of dates
 * @returns a new array of unique dates
 */
export const removeDuplicateDates = (publicationDates: Date[]): Date[] => {
  const datesAsStrings: string[] = [];
  const uniqueDates: Date[] = [];
  for (const date of publicationDates) {
    if (!datesAsStrings.includes(date.toString())) {
      datesAsStrings.push(date.toString());
      uniqueDates.push(date);
    }
  }

  return uniqueDates;
};

/**
 * handlePubDateChangeForWeekendEdition when weekend edition enabled:
 * 1. removes the clicked date, the row clicked in app
 * 2. adds a date chosen form the date picker
 * 3. deletes duplicate dates from the array
 *
 * @param selectedPublicationDate date selected in date picker
 * @param publicationDates array containing publication dates
 * @param i row clicked in app, the index of the element in publicationDates array
 * @returns a new array removing publicationDates[i], adding selectedPublicationDate
 */
export const handlePubDateChangeForWeekendEdition = (
  selectedPublicationDate: Date,
  publicationDates: Date[],
  i: number
): Date[] => {
  let newPubDates = removeRowClickForWeekendEdition(publicationDates, i);
  newPubDates = addPublicationDates(selectedPublicationDate, newPubDates);
  newPubDates = removeDuplicateDates(newPubDates);

  return newPubDates;
};

/**
 * Checks if the given date should be disabled in the confirm schedule step
 */
export const shouldDisableDate = ({
  day,
  newspaper,
  user,
  notice,
  noticeType
}: {
  day: Date;
  newspaper: ESnapshot<EOrganization> | undefined;
  user: ESnapshot<EUser> | null | undefined;
  notice: DataWithNoticeType;
  noticeType: CustomNoticeFilingType | null | undefined;
}) => {
  // Super users (advertisers or publishers) are not subject to publication
  // date restrictions
  if (exists(user) && userIsSuper(user)) {
    return false;
  }

  const userModel = exists(user)
    ? getModelFromSnapshot(UserModel, getFirebaseContext(), user)
    : null;

  if (!exists(newspaper)) {
    return false;
  }

  const { deadlines, deadlineOverrides = {}, iana_timezone } = newspaper.data();
  if (!deadlines || !day || !iana_timezone) {
    return true;
  }

  const isNonPublishingDay = disableNonPublishingDays(
    day,
    publishingDayEnumValuesFromDeadlines(deadlines),
    newspaper.data().deadlineOverrides
  );

  /**
   * NOTE: I'm not sure why we're adding an hour here, and I'm not sure if we're properly
   * accounting for the newspaper vs. user device timezone when setting the hour.
   * Previously we were changing the hour of the day passed in directly, which
   * was causing the date to shift later one hour every time a new date was added to the
   * publication dates in the UI. Leaving this adjustment for the deadline check,
   * but removed the date shifting.
   */
  const testTimestamp = new Date(day).setHours(day.getHours() + 1);
  const testDate = new Date(testTimestamp);
  const isAfterDeadline = getIsAfterPublishingDeadline(
    testDate,
    deadlines,
    deadlineOverrides,
    iana_timezone,
    notice,
    newspaper
  );

  const isDayAllowedByNoticeType = isAllowedPublicationDay(day, noticeType);

  if (!userModel?.isPublisher && isAfterDeadline) {
    return true;
  }

  return !isDayAllowedByNoticeType || isNonPublishingDay;
};
